/**
 * Notification centre — the bell in the header and its feed.
 *
 * Rendered through AppShell's `headerActions` slot, so the shared shell that all
 * four apps use is untouched.
 *
 * Freshness: the feed is realtime, so the panel says whether it is actually
 * streaming. When the channel is down the chip reads "offline", never "live" —
 * a quiet feed must not be mistaken for a calm one.
 *
 * Reading is explicit, not incidental. Opening the panel does NOT mark anything
 * read: a customer who glances at a bell to see whether it is worth their time
 * has not read six notices, and clearing the badge for them destroys the only
 * signal they had. A row is marked read when it is opened, and "Mark all read"
 * is there for the sweep. This is a decision, not an omission.
 */
import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDismissable } from '@pazportal/ui';
import { Freshness } from './Freshness';
import { useI18n } from '../i18n/I18nProvider';
import { timeAgo } from '../lib/formatters';
import { useNotifications, type NotificationRecord } from '../hooks/useNotifications';

const SEVERITY_DOT: Record<NotificationRecord['severity'], string> = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  critical: 'bg-notification',
};

function BellIcon({ hasUnread }: { hasUnread: boolean }) {
  return (
    <svg
      className="h-4 w-4"
      style={{ color: hasUnread ? 'var(--notification)' : 'var(--text-muted)' }}
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
    </svg>
  );
}

export function NotificationBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { notifications, unreadCount, isLoading, freshness, lastEventAt, readOnly, markRead, markAllRead } =
    useNotifications();

  // Escape closes and returns focus to the bell; a click outside just closes.
  useDismissable(containerRef, open, () => setOpen(false), buttonRef);

  const openItem = (item: NotificationRecord) => {
    if (!item.read_at && !readOnly) markRead(item.id);
    setOpen(false);
    if (item.href) navigate(item.href);
  };

  const label = unreadCount > 0
    ? t('notifications.aria_unread', { n: unreadCount })
    : t('notifications.aria_none');

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        className="relative inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:h-8 md:w-8"
      >
        <BellIcon hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 min-w-[1rem] rounded-full px-1 text-[10px] font-bold leading-4 text-text-inverse"
            style={{ backgroundColor: 'var(--notification)' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={t('notifications.title')}
          className="absolute right-0 z-50 mt-2 flex max-h-[28rem] w-[min(22rem,calc(100vw-2rem))] flex-col rounded-lg border border-border bg-surface shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-divider px-3 py-2">
            <h2 className="text-sm font-medium text-text">{t('notifications.title')}</h2>
            {unreadCount > 0 && !readOnly && (
              <button
                type="button"
                onClick={markAllRead}
                className="rounded-sm px-2 py-1 text-xs font-medium text-primary hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {t('notifications.mark_all_read')}
              </button>
            )}
          </div>

          <div className="border-b border-divider px-3 py-2">
            <Freshness
              lastUpdatedAt={lastEventAt}
              forcedState={freshness}
              streaming={freshness === 'live'}
              label={t('notifications.title')}
            />
          </div>

          <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {isLoading && (
              <li className="px-3 py-4 text-sm text-text-muted">{t('common.loading')}</li>
            )}
            {!isLoading && notifications.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-text-muted">{t('notifications.empty')}</li>
            )}
            {notifications.map((item) => (
              <li key={item.id} className="border-b border-divider last:border-b-0">
                <button
                  type="button"
                  onClick={() => openItem(item)}
                  className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
                    item.read_at ? '' : 'bg-surface-2'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[item.severity]}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm leading-snug text-text">{item.title}</span>
                    {item.body && (
                      <span className="mt-0.5 block truncate text-xs text-text-muted">{item.body}</span>
                    )}
                    <span className="mt-1 block text-[11px] text-text-faint">
                      {timeAgo(item.created_at)}
                      {!item.read_at && ` · ${t('notifications.unread')}`}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
